// App Manager Class for the App Section
// Handles business logic and state management for the projects dashboard
// Follows the Manager and Component pattern adapted from AgentGroupChat

import { Manager, logger, envUtils, normalizeAppType } from "../../../core";
import { navigationManager } from "../../../managers/navigation-manager";

export class AppManager extends Manager {
  constructor() {
    super();
    this.projects = [];
    this.isLoading = false;
    this.searchTerm = "";
    this.sortBy = "lastModified";
    this.favoritesFilter = "all"; // 'all' | 'favorites' | 'nonFavorites'
    this.ownedFilter = "all"; // 'all' | 'owned' | 'nonOwned'
    this.tagsFilter = []; // Array of tag strings (AND filter)
    /** When true, favorited apps sort before others, then existing sort order applies within each group. */
    this.pinFavoritesTop = false;
    this.isMobile = false;
    this.currentUserId = null;
    this.currentUser = null;
  }

  /**
   * Initialize global variables and setup
   */
  async initGlobalVariables() {
    // Navigation manager will be connected via the React hook adapter
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log(
      {
        tags: "manager|app|init",
        color1: "cyan",
      },
      "Initializing AppSection manager states"
    );

    try {
      this.projects = [];

      logger.log(
        {
          tags: "manager|app|ready",
          color1: "lime",
        },
        `AppSection manager initialized with ${this.projects.length} projects`
      );
    } catch (error) {
      logger.error(
        {
          tags: "manager|app|error",
          color1: "red",
          color2: "orange",
          includeSource: true,
        },
        "Failed to initialize AppSection manager states:",
        error
      );

      throw error;
    }
  }


  /**
   * Load projects for the current user based on memberships.
   */
  async loadUserProjects(user) {
    if (!user?._id && !user?.id) {
      return;
    }
    this.isLoading = true;
    const userId = user._id || user.id;
    this.currentUserId = userId;
    this.currentUser = user;

    try {
      // 1) Get memberships for user
      const memRes = await fetch(`/api/app-memberships?userId=${encodeURIComponent(userId)}`, {
        credentials: 'include',
      });
      const memberships = memRes.ok ? await memRes.json() : [];

      const appIds = memberships.map((m) => m.appId).filter(Boolean);
      if (appIds.length === 0) {
        this.projects = [];
        return;
      }

      // 2) Fetch apps by IDs
      const appsRes = await fetch(`/api/apps/by-ids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ appIds }),
      });
      const appsData = appsRes.ok ? await appsRes.json() : { apps: [] };
      const apps = appsData.apps || [];

      // 3) Merge membership info into apps
      const memMap = new Map();
      memberships.forEach((m) => {
        memMap.set(String(m.appId), m);
      });

      const merged = apps.map((app) => {
        const mem = memMap.get(String(app._id));
        return {
          id: app._id,
          name: app.name,
          description: app.description,
          template: app.template,
          appType: app.appType,
          likes: app.likes || 0,
          viewsNormal: app.viewsNormal || 0,
          viewsSharing: app.viewsSharing || 0,
          viewsEmbedHeader: app.viewsEmbedHeader || 0,
          viewsEmbedNoHeader: app.viewsEmbedNoHeader || 0,
          viewsSettings: app.viewsSettings || 0,
          totalSizeBytes: app.totalSizeBytes || 0,
          isFavorited: mem?.settings?.isFavorited || false,
          isPublic: app.isPublic || false,
          allowRemixing: app.allowRemixing !== false, // Default to true if undefined
          ownerName: app.ownerUsername || 'Unknown',
          createdAt: app.createdAt,
          modifiedAt: app.modifiedAt,
          isOwner: mem?.role === 'owner' || (app.ownerId && String(app.ownerId) === String(userId)),
          isAdmin: mem?.role === 'admin' || mem?.role === 'site_admin',
          membershipRole: mem?.role || 'member',
          settings: app.settings || {},
          tags: Array.isArray(app.tags) ? app.tags : [],
        };
      });

      this.projects = merged;
    } catch (error) {
      logger.error({ tags: 'manager|app|load', color1: 'red' }, 'Failed to load user projects', error);
      this.projects = [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Update search term
   */
  setSearchTerm(term) {
    this.searchTerm = term;

    logger.log(
      {
        tags: "manager|app|action",
        color1: "cyan",
      },
      `Search term updated: "${term}"`
    );
  }

  /**
   * Update sort option
   */
  setSortBy(sortOption) {
    this.sortBy = sortOption;

    logger.log(
      {
        tags: "manager|app|action",
        color1: "cyan",
      },
      `Sort option updated: ${sortOption}`
    );
  }

  /**
   * Set mobile state
   */
  setIsMobile(isMobile) {
    this.isMobile = isMobile;
  }

  /**
   * Get views information for an app
   * @param {Object} app - The app object
   * @returns {Object} Views data with individual counters and total
   */
  getViewsInfo(app) {
    if (!app) return null;

    const views = {
      normal: app.viewsNormal || 0,
      sharing: app.viewsSharing || 0,
      embedHeader: app.viewsEmbedHeader || 0,
      embedNoHeader: app.viewsEmbedNoHeader || 0,
      settings: app.viewsSettings || 0,
    };

    views.total = views.normal + views.sharing + views.embedHeader + views.embedNoHeader + views.settings;

    return views;
  }

  /**
   * Toggle like status for an app
   */
  async toggleLikeApp(appId) {
    try {
      logger.log(
        {
          tags: "manager|app|toggle-like",
          color1: "cyan",
        },
        `Toggling like for app: ${appId}`
      );

      const response = await fetch(`/api/apps/${appId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to toggle like');
      }

      // Update the app in our local state to reflect the new like count and status
      const appIndex = this.projects.findIndex((app) => {
        const localAppId = app?.id || app?._id;
        return String(localAppId) === String(appId);
      });
      if (appIndex !== -1) {
        this.projects[appIndex].likes = data.likes;
        this.projects[appIndex].userLiked = data.liked;
      }

      logger.log(
        {
          tags: "manager|app|toggle-like|success",
          color1: "lime",
        },
        `Successfully toggled like for app ${appId}, liked: ${data.liked}, count: ${data.likes}`
      );

      return data;
    } catch (error) {
      logger.error(
        {
          tags: "manager|app|toggle-like|error",
          color1: "red",
        },
        `Failed to toggle like for app ${appId}:`, error
      );
      throw error;
    }
  }

  /**
   * Set favorites filter: 'all' | 'favorites' | 'nonFavorites'
   */
  setFavoritesFilter(value) {
    this.favoritesFilter = value || "all";
    logger.log(
      { tags: "manager|app|action", color1: "cyan" },
      `Favorites filter: ${this.favoritesFilter}`
    );
  }

  /**
   * Set owned filter: 'all' | 'owned' | 'nonOwned'
   */
  setOwnedFilter(value) {
    this.ownedFilter = value || "all";
    logger.log(
      { tags: "manager|app|action", color1: "cyan" },
      `Owned filter: ${this.ownedFilter}`
    );
  }

  /**
   * Pin favorited apps to the top of the list (before secondary sort).
   * @param {boolean} value
   */
  setPinFavoritesTop(value) {
    this.pinFavoritesTop = !!value;
    logger.log(
      { tags: "manager|app|action", color1: "cyan" },
      `Pin favorites to top: ${this.pinFavoritesTop}`
    );
  }

  /**
   * Set tags filter (array of strings). App must have ALL tags to pass.
   */
  setTagsFilter(tags) {
    this.tagsFilter = Array.isArray(tags) ? tags : [];
    logger.log(
      { tags: "manager|app|action", color1: "cyan" },
      `Tags filter: ${this.tagsFilter.join(", ") || "(none)"}`
    );
  }

  /**
   * Toggle favorite status for a project (optimistic approach)
   */
  async toggleFavorite(projectId, onStateChange) {
    const project = this.projects.find((p) => p.id === projectId);
    if (!project || !this.currentUserId) {
      return;
    }

    const originalValue = project.isFavorited;
    const nextValue = !originalValue;

    logger.log(
      {
        tags: "manager|app|action",
        color1: "cyan",
      },
      `Optimistically toggling favorite for project: ${project.name} (${originalValue} → ${nextValue})`
    );

    // OPTIMISTIC UPDATE: Immediately update local state and trigger UI re-render
    project.isFavorited = nextValue;
    if (onStateChange) {
      onStateChange();
    }

    // Make API call in background (non-blocking)
    this._makeFavoriteApiCall(projectId, nextValue, originalValue, onStateChange);
  }

  /**
   * Private method to handle the background API call for favoriting
   */
  async _makeFavoriteApiCall(projectId, desiredValue, originalValue, onStateChange) {
    try {
      logger.log(
        {
          tags: "manager|app|favorite|api",
          color1: "blue",
        },
        `Making API call to set favorite=${desiredValue} for app ${projectId}`
      );

      const res = await fetch('/api/app-memberships/favorite', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: this.currentUserId,
          appId: projectId,
          isFavorited: desiredValue,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to update favorite');
      }

      logger.log(
        {
          tags: "manager|app|favorite|success",
          color1: "lime",
        },
        `Successfully updated favorite status for app ${projectId} to ${!!data.membership?.isFavorited}`
      );

      // SUCCESS: Keep the optimistic change (UI already updated correctly)

    } catch (error) {
      logger.error(
        {
          tags: "manager|app|favorite|error",
          color1: "red",
        },
        `Failed to update favorite status for app ${projectId}, reverting optimistic change`,
        error
      );

      // FAILURE: Revert the optimistic change
      const project = this.projects.find((p) => p.id === projectId);
      if (project) {
        project.isFavorited = originalValue;
        if (onStateChange) {
          onStateChange();
        }
      }
    }
  }

  /**
   * Navigate to workspace for a project
   */
  async openWorkspace(projectId) {
    const project = this.projects.find((p) => p.id === projectId);

    logger.log(
      {
        tags: "manager|app|action",
        color1: "cyan",
      },
      `Opening workspace for project: ${project?.name || "Unknown"}`
    );

    if (projectId) {
      await navigationManager.navigateToPath(`/workspace/${projectId}`);
    } else {
      await navigationManager.navigateTo("workspace");
    }
  }

  /**
   * Navigate to playspace for a project
   * Mirrors workspace flow but targets playspace route (placeholder for now)
   */
  async openPlayspace(projectId) {
    const project = this.projects.find((p) => p.id === projectId);

    logger.log(
      {
        tags: "manager|app|action",
        color1: "cyan",
      },
      `Opening playspace for project: ${project?.name || "Unknown"}`
    );

    if (projectId) {
      await navigationManager.navigateToPath(`/playspace/${projectId}`);
    } else {
      await navigationManager.navigateTo("playspace");
    }
  }

  /**
   * Navigate to settings for a project
   * In the future, this would open project settings modal or navigate to settings page
   */
  async openSettings(projectId) {
    const project = this.projects.find((p) => p.id === projectId);

    logger.log(
      {
        tags: "manager|app|action",
        color1: "cyan",
      },
      `Opening settings for app: ${project?.name || "Unknown"}`
    );

    if (projectId) {
      await navigationManager.navigateToAppSettings(projectId);
    } else {
      await navigationManager.navigateTo("app-settings");
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  /**
   * Get projects filtered and sorted for display
   */
  getDisplayProjects() {
    return this.projects
      .filter((project) => {
        // Local apps are desktop-only; do not list them on the website /app grid.
        if (
          !envUtils.device.isDesktopApp() &&
          normalizeAppType(project.appType) === "local"
        ) {
          return false;
        }
        // Filter by favorites
        if (this.favoritesFilter === "favorites" && !project.isFavorited) {
          return false;
        }
        if (this.favoritesFilter === "nonFavorites" && project.isFavorited) {
          return false;
        }
        // Filter by ownership
        if (this.ownedFilter === "owned" && !project.isOwner) {
          return false;
        }
        if (this.ownedFilter === "nonOwned" && project.isOwner) {
          return false;
        }
        // Filter by tags (app must have ALL selected tags)
        if (this.tagsFilter.length > 0) {
          const projectTags = (project.tags || []).map((t) => String(t).toLowerCase());
          const hasAll = this.tagsFilter.every((tag) =>
            projectTags.includes(String(tag).toLowerCase())
          );
          if (!hasAll) return false;
        }
        // Filter by search term
        const search = this.searchTerm.toLowerCase();
        return (
          project.name?.toLowerCase().includes(search) ||
          (project.description || "").toLowerCase().includes(search)
        );
      })
      .sort((a, b) => {
        if (this.pinFavoritesTop) {
          const af = !!a.isFavorited;
          const bf = !!b.isFavorited;
          if (af !== bf) {
            return af ? -1 : 1;
          }
        }
        if (this.sortBy === "name") {
          return (a.name || "").localeCompare(b.name || "");
        }
        if (this.sortBy === "created") {
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }
        // Default: lastModified
        return new Date(b.modifiedAt || 0) - new Date(a.modifiedAt || 0);
      });
  }

  /**
   * Handle creating a new app directly (not via AI)
   * Shows under construction modal since this feature isn't ready yet
   */
  handleCreateNewApp(showUnderConstructionModal) {
    logger.log(
      {
        tags: "manager|app|action|under-construction",
        color1: "yellow",
        color2: "orange",
      },
      "Create New App button clicked - showing under construction modal"
    );

    // Show the under construction modal
    if (showUnderConstructionModal) {
      showUnderConstructionModal();
    }

    // Log for debugging
    logger.log({
      tags: "manager|app|construction",
      color1: "orange"
    }, "App creation under construction", {
      method: "manual",
      source: "createNewAppButton",
      timestamp: new Date().toISOString(),
      note: "Feature not yet implemented - showing under construction modal",
    });
  }

  /**
   * Delete an app
   */
  async deleteApp(appId) {
    if (!appId) return false;
    try {
      const res = await fetch(`/api/apps/${appId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to delete app');
      }
      return true;
    } catch (error) {
      logger.error(
        { tags: "manager|app|delete", color1: "red" },
        "Delete app failed",
        error
      );
      return false;
    }
  }

  /**
   * Get template display name
   */
  getTemplateDisplayName(template) {
    const names = {
      browser: "Browser App",
      website: "Website",
      desktop: "Desktop App",
    };
    return names[template] || template;
  }
}
